import { useEffect, useMemo, useState } from "react";
import { useSocket } from "../../features/socket/SocketProvider";
import type { CalendarEvent } from "./calendarTypes";
import { loadEvents, fetchRemoteEvents, persistEvents } from "./calendarStorage";
import { mergeEventLists, parseTimeString } from "./calendarUtils";

// The home-page widget for a favorited calendar channel: a compact month grid with a dot on every day that has
// events, and the next few events underneath (or the picked day's). Read-only: opening the channel is for editing.

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const pad = (n: number) => String(n).padStart(2, "0");
const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const eventDay = (ev: CalendarEvent) => String(ev.date || "").slice(0, 10);

/** Events store their time as 24-hour "HH:MM"; show it the way the calendar channel does ("9:05 AM"). */
function timeLabel(time?: string): string {
  const t = parseTimeString(time);
  return t ? `${t.timeHour}:${pad(t.timeMinute)} ${t.timePeriod}` : "";
}

/** Minutes since midnight, so events sort by time within a day; untimed events sort first. */
function minutesOf(ev: CalendarEvent): number {
  const match = /^(\d{1,2}):(\d{2})/.exec(ev.time || "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : -1;
}

type Props = {
  /** Fully-qualified channel id (space:channel), the key the calendar channel stores its events under. */
  fid: string;
  spaceName: string;
  channelName: string;
  onOpen: () => void;
  /** Hover-revealed remove button, styled by the home page's `group` wrapper. */
  onRemove: () => void;
};

export default function CalendarFavoriteCard({ fid, spaceName, channelName, onOpen, onRemove }: Props) {
  const { socket } = useSocket();
  const [events, setEvents] = useState<CalendarEvent[]>(() => loadEvents(fid));
  const [monthOffset, setMonthOffset] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const remote = await fetchRemoteEvents(fid);
      if (!alive || !remote) return;
      setEvents((prev) => {
        const merged = mergeEventLists(prev, remote, new Set());
        persistEvents(fid, merged);
        return merged;
      });
    })();
    return () => { alive = false; };
  }, [fid]);

  // Live pushes from the calendar channel (the same event the channel view listens to).
  useEffect(() => {
    if (!socket) return undefined;
    const handler = (payload: { channelId?: string; events?: CalendarEvent[] }) => {
      if (payload?.channelId !== fid || !Array.isArray(payload.events)) return;
      setEvents((prev) => mergeEventLists(prev, payload.events || [], new Set()));
    };
    socket.on("calendar:update", handler);
    return () => { socket.off("calendar:update", handler); };
  }, [socket, fid]);

  const today = new Date();
  const todayKey = dayKey(today);
  const cursor = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = cursor.toLocaleDateString([], { month: "long", year: "numeric" });

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const key = eventDay(ev);
      if (!key) continue;
      const list = map.get(key) || [];
      list.push(ev);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => minutesOf(a) - minutesOf(b));
    return map;
  }, [events]);

  // Grid cells: leading blanks up to the first weekday, then every day of the month.
  const cells: (Date | null)[] = [];
  for (let i = 0; i < cursor.getDay(); i++) cells.push(null);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const listed: { key: string; ev: CalendarEvent }[] = [];
  if (picked) {
    for (const ev of byDay.get(picked) || []) listed.push({ key: picked, ev });
  } else {
    const keys = [...byDay.keys()].filter((k) => k >= todayKey).sort();
    for (const key of keys) {
      for (const ev of byDay.get(key) || []) {
        if (listed.length < 4) listed.push({ key, ev });
      }
    }
  }
  const dayLabel = (key: string) => {
    if (key === todayKey) return "Today";
    const [y, m, d] = key.split("-").map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <div className="flex h-full min-w-0 flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <button type="button" className="min-w-0 text-left" onClick={onOpen} title="Open calendar">
          <p className="truncate text-xs text-emerald-300/80">Calendar / {spaceName}</p>
          <div className="truncate text-lg font-medium text-fg">#{channelName}</div>
        </button>
        <button
          type="button"
          className="shrink-0 rounded-full border border-transparent p-1 text-fg-3 opacity-0 transition-opacity hover:text-fg group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
          title="Remove favorite"
          aria-label="Remove favorite"
          onClick={onRemove}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <path d="m18 6-12 12" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div className="flex items-center justify-between text-sm">
        <button type="button" className="rounded-full px-2 py-0.5 text-fg-2 hover:bg-white/10 hover:text-fg" aria-label="Previous month" onClick={() => { setMonthOffset((m) => m - 1); setPicked(null); }}>‹</button>
        <span className="font-medium text-fg">{monthLabel}</span>
        <button type="button" className="rounded-full px-2 py-0.5 text-fg-2 hover:bg-white/10 hover:text-fg" aria-label="Next month" onClick={() => { setMonthOffset((m) => m + 1); setPicked(null); }}>›</button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 text-center text-meta">
        {WEEKDAYS.map((w, i) => <div key={i} className="text-fg-3">{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />;
          const key = dayKey(d);
          const count = byDay.get(key)?.length || 0;
          const isToday = key === todayKey;
          const isPicked = key === picked;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setPicked(isPicked ? null : key)}
              aria-label={`${d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}${count ? `, ${count} event${count === 1 ? "" : "s"}` : ""}`}
              aria-pressed={isPicked}
              className={`mx-auto flex h-8 w-8 flex-col items-center justify-center rounded-full text-sm transition-colors ${
                isPicked ? "bg-emerald-500/25 text-fg" : isToday ? "bg-emerald-500 font-semibold text-black" : "text-fg-2 hover:bg-white/10"
              }`}
            >
              <span className="leading-none">{d.getDate()}</span>
              <span className={`mt-0.5 h-1 w-1 rounded-full ${count ? (isToday && !isPicked ? "bg-black/60" : "bg-emerald-400") : "bg-transparent"}`} />
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 text-sm">
        <p className="text-meta text-fg-3">{picked ? dayLabel(picked) : "Coming up"}</p>
        {listed.length === 0 ? (
          <div className="surface-inset rounded-xl px-3 py-2 text-fg-3">{picked ? "Nothing planned that day." : "No upcoming events."}</div>
        ) : (
          listed.map(({ key, ev }) => (
            <div key={`${key}-${ev.id}`} className="surface-inset flex items-baseline gap-3 rounded-xl px-3 py-1.5">
              <span className="w-24 shrink-0 text-meta text-fg-3">{picked ? (timeLabel(ev.time) || "All day") : dayLabel(key)}</span>
              <span className="min-w-0 flex-1 truncate text-fg">{ev.title}</span>
              {!picked && timeLabel(ev.time) && <span className="shrink-0 text-meta text-fg-3">{timeLabel(ev.time)}</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
